// src/context/ModalProvider.tsx
import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from 'react';
import type { ScheduleMeetingType } from '../lib/schedule/meetingTypes';

import AuthModal from '../components/modals/AuthModal';
import SettingsModal from '../components/modals/SettingsModal';
import RecurringScheduleModal from '../components/modals/RecurringScheduleModal';
import PublicTimeSlotModal from '../components/modals/PublicTimeSlotModal';
import ConnectIntegrationModal from '../components/modals//ConnectIntegrationModal';
import { NotificationsModal } from '../components/dashboard/NotificationsCard';
import GlobalSearchModal from '../components/modals/GlobalSearchModal';

type ModalKey =
  | 'auth'
  | 'settings'
  | 'recurring-schedule'
  | 'public-timeslot'
  | 'integration'
  | 'notifications'
  | 'global-search'
  | null;

export interface RecurringScheduleModalData {
  onCreateRecurring: (config: any) => void;
  availableTypes: ScheduleMeetingType[];
  timeSlots: string[];
}

export interface PublicTimeSlotModalData {
  date: Date;
  times: string[];
  onSelect: (t: string) => void;
}

export interface AuthModalData {
  email?: string;
}

interface Ctx {
  open: (key: Exclude<ModalKey, null>, data?: any) => void;
  close: () => void;
  recurringScheduleData: RecurringScheduleModalData | null;
  publicTimeSlotData: PublicTimeSlotModalData | null;
}

const ModalCtx = createContext<Ctx>(undefined as any);

export function ModalProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<ModalKey>(null);

  const [recurringScheduleData, setRecurringScheduleData] =
    useState<RecurringScheduleModalData | null>(null);

  const [publicTimeSlotData, setPublicTimeSlotData] =
    useState<PublicTimeSlotModalData | null>(null);
  const [authModalData, setAuthModalData] =
    useState<AuthModalData | null>(null);

  const open = (key: Exclude<ModalKey, null>, data?: any) => {
    switch (key) {
      case 'auth':
        setAuthModalData((data ?? null) as AuthModalData | null);
        break;
      case 'recurring-schedule':
        setRecurringScheduleData(data ?? null);
        break;
      case 'public-timeslot':
        setPublicTimeSlotData(data ?? null);
        break;
      default:
        break;
    }
    setActive(key);
  };

  const close = () => {
    setActive(null);
    setAuthModalData(null);
    setRecurringScheduleData(null);
    setPublicTimeSlotData(null);
  };

  useEffect(() => {
    const closeAll = () => close();
    window.addEventListener('modals:close-all', closeAll as EventListener);
    return () => window.removeEventListener('modals:close-all', closeAll as EventListener);
  }, []);

  return (
    <ModalCtx.Provider
      value={{
        open,
        close,
        recurringScheduleData,
        publicTimeSlotData,
      }}
    >
      {children}

      <AuthModal
        open={active === 'auth'}
        onClose={close}
        initialEmail={authModalData?.email ?? ''}
      />

      {active === 'settings' && <SettingsModal />}

      {active === 'recurring-schedule' && recurringScheduleData && (
        <RecurringScheduleModal {...recurringScheduleData} />
      )}

      {active === 'public-timeslot' && publicTimeSlotData && (
        <PublicTimeSlotModal {...publicTimeSlotData} />
      )}

      {active === 'integration' && <ConnectIntegrationModal />}

      <NotificationsModal open={active === 'notifications'} onClose={close} />

      <GlobalSearchModal open={active === 'global-search'} onClose={close} />
    </ModalCtx.Provider>
  );
}

export const useModal = () => useContext(ModalCtx);
